import React, { useState, useEffect } from 'react';
import { apiFetch, useAuth } from '../../App';
import { Coins, TrendingUp, TrendingDown, Info } from 'lucide-react';

export default function PawsCredits() {
  const { user } = useAuth();
  const [data, setData] = useState({ balance: 0, ledger: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/credits').then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="paws-credits" className="max-w-4xl">
      <h1 className="font-outfit text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>My Credits</h1>
      
      {/* Balance Card */}
      <div className="rounded-3xl p-8 mb-8 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #8B5CF6 100%)' }} data-testid="credit-balance-card">
        <div className="relative z-10">
          <p className="text-white/80 text-sm mb-1">Available Balance</p>
          <p className="font-outfit text-5xl font-extrabold mb-2">₹{data.balance.toFixed(0)}</p>
          <p className="text-white/70 text-sm">Earned from {data.ledger.filter(l => l.delta > 0).length} transactions</p>
        </div>
        <div className="absolute top-4 right-4 w-24 h-24 rounded-full bg-white/10" />
        <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-full bg-white/5" />
      </div>

      {/* How Credits Work */}
      <div className="petfolk-card p-6 mb-8" data-testid="credits-info">
        <div className="flex items-center gap-2 mb-3">
          <Info size={18} className="text-petfolk-orange" />
          <h2 className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>How Credits Work</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <div className="p-3 rounded-xl" style={{ background: 'rgba(255,140,66,0.08)' }}>
            <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Earn 5%</p>
            <p style={{ color: 'var(--text-secondary)' }}>On every completed booking</p>
          </div>
          <div className="p-3 rounded-xl" style={{ background: 'rgba(139,92,246,0.08)' }}>
            <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Redeem up to 20%</p>
            <p style={{ color: 'var(--text-secondary)' }}>On your next booking</p>
          </div>
          <div className="p-3 rounded-xl" style={{ background: 'rgba(32,178,170,0.08)' }}>
            <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>No Expiry</p>
            <p style={{ color: 'var(--text-secondary)' }}>Your credits never expire</p>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <h2 className="font-outfit text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Transaction History</h2>
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}</div>
      ) : data.ledger.length === 0 ? (
        <div className="petfolk-card p-8 text-center">
          <p style={{ color: 'var(--text-secondary)' }}>No transactions yet. Book a grooming session to start earning credits!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.ledger.map((l, i) => (
            <div key={i} className="petfolk-card p-4 flex items-center gap-4" data-testid={`txn-${i}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${l.delta > 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                {l.delta > 0 ? <TrendingUp size={18} className="text-green-600" /> : <TrendingDown size={18} className="text-red-500" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{l.reason}</p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{new Date(l.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>
              <div className="text-right">
                <p className={`font-outfit font-bold ${l.delta > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {l.delta > 0 ? '+' : ''}₹{l.delta.toFixed(0)}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Bal: ₹{l.balanceAfter.toFixed(0)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
